/**
 * The assistant's keyframe operations (spec 036): `moveSprite`,
 * `setSpriteState`, `showSprite` and `hideSprite`, and spec 038's
 * `flipSprite`, time- and name-based,
 * writing ordinary authored keyframes by spec 006's write rules
 * (`keyframeWrite.ts`: the slot invariant, the orientation fork and the
 * played value). No operation takes a raw track. Each is pure, takes seconds
 * (stored as whole ms), derives its key ids from the document
 * (`motionKeyframeId`, D-11) and returns `createdIds: []`.
 */
import type { Keyframe, KeyframeEasing, Orientation, Sprite } from '../@types';
import { candidateKeyframes } from '../utils/interpolate';
import {
  forkOrientation, isUntaggedKeyframe, KEYFRAME_EASINGS, keyframeBaseValue, keyframeValueAt, putKeyframe, type NewKeyframeId,
} from '../utils/keyframeWrite';
import { findSprite } from './getSprite';
import { motionKeyframeId, scopeText, trackOf, withTrack } from './operations';
import { ORIENTATIONS, POSITION_RANGE } from './placement';
import { storedProperty, toStoredValue, type KeyProperty } from './keyNames';
import { fmtS, RANGES, secToMs } from './units';
import type { JsonSchema, OperationError, OperationResult, SceneDocument, SceneOperation } from './types';

// ─── Schema ─────────────────────────────────────────────────────────────────

const str = (description: string): JsonSchema => ({ type: 'string', description });
const num = (description: string): JsonSchema => ({ type: 'number', description });
const obj = (properties: Record<string, JsonSchema>, required: string[] = [], description?: string): JsonSchema => ({
  type: 'object', ...(description ? { description } : {}), properties, required, additionalProperties: false,
});

/** Ranges of the keyframe operations, in operation units (D-2). */
export const KEY_RANGES = {
  /** `at` and `until`, seconds from the scene start. */
  time: [0, 3600],
  /** `over` and `fade`, seconds: above 0 and up to 60. */
  span: [0, 60],
  x: POSITION_RANGE,
  y: POSITION_RANGE,
  scale: RANGES.scale,
  rotation: RANGES.rotation,
  opacity: [0, 1],
  hingeX: RANGES.hinge,
  hingeY: RANGES.hinge,
} as const satisfies Record<string, readonly [number, number]>;

const r = (k: keyof typeof KEY_RANGES) => `${KEY_RANGES[k][0]}–${KEY_RANGES[k][1]}`;

const SPRITE_SCHEMA = str('The sprite\'s id or name, e.g. Pirate.');
const AT_SCHEMA = num(`Seconds from the scene start (${r('time')}).`);
const KEY_ORIENTATION_SCHEMA: JsonSchema = {
  type: 'string',
  enum: ORIENTATIONS,
  description: 'Only this orientation (portrait phone, landscape phone or desktop) changes; the others play as before. Omit to change every orientation that has no keys of its own for that property.',
};
const KEY_EASE_SCHEMA: JsonSchema = {
  type: 'string',
  enum: KEYFRAME_EASINGS,
  description: 'How it glides over `over`: linear, easeIn, easeOut or easeInOut; only with over.',
};

/** The `to` fields in the order the operation writes and the summary names them. */
const MOVE_FIELDS = ['x', 'y', 'scale', 'rotation', 'opacity', 'hingeX', 'hingeY'] as const;
type MoveField = typeof MOVE_FIELDS[number];

const TO_SCHEMA = obj({
  x: num(`Hinge x in % of the stage width (${r('x')}).`),
  y: num(`Hinge y in % of the stage height (${r('y')}).`),
  scale: num(`Factor on the box size, 1 = the box as sized (${r('scale')}).`),
  rotation: num(`Degrees clockwise (${r('rotation')}).`),
  opacity: num(`0 = invisible, 1 = fully visible (${r('opacity')}).`),
  hingeX: num(`The hinge across the box: 0 = left edge, 0.5 = centre, 1 = right edge (${r('hingeX')}).`),
  hingeY: num(`The hinge down the box: 0 = top edge, 0.5 = centre, 1 = bottom edge (${r('hingeY')}).`),
}, [], 'The values it has at `at`; at least one.');

// ─── Helpers ────────────────────────────────────────────────────────────────

type Errors = OperationError[];
type Scope = Orientation | undefined;
const fail = (errors: Errors): OperationResult => ({ ok: false, errors });
const err = (code: OperationError['code'], message: string, path?: string): OperationError =>
  (path ? { code, message, path } : { code, message });

/** The tag a scope's keys carry: portrait or landscape, else untagged (desktop has no tag that overrides, 006). */
const tagOf = (scope: Scope): 'portrait' | 'landscape' | undefined =>
  (scope === 'portrait' || scope === 'landscape' ? scope : undefined);
/** The orientation whose playing value a scope's keys start from. */
const playsIn = (scope: Scope): Orientation => tagOf(scope) ?? 'desktop';
/** A key in the set a tag writes: its own tag, or untagged and desktop-tagged for the base. */
const inSet = (k: Keyframe, property: string, tag: string | undefined) =>
  k.property === property && (tag ? k.orientation === tag : isUntaggedKeyframe(k));
const hasOwn = (track: readonly Keyframe[], property: string, o: Orientation) =>
  track.some((k) => k.property === property && k.orientation === o);

function inRange(errors: Errors, value: number | undefined, range: readonly [number, number], path: string, unit = '') {
  if (value === undefined) return;
  if (value < range[0] || value > range[1]) errors.push(err('out_of_range', `${path} must be ${range[0]}–${range[1]}${unit}`, path));
}

/** `over` and `fade`: above 0 and up to 60 s. */
function inSpan(errors: Errors, value: number | undefined, path: string) {
  if (value === undefined) return;
  if (!(value > 0) || value > KEY_RANGES.span[1]) errors.push(err('out_of_range', `${path} must be above 0 and at most ${KEY_RANGES.span[1]} s`, path));
}

/** D-10: the sprite by id or name, or the lookup's error at `sprite`, listing every sprite. */
function lookUp(errors: Errors, doc: SceneDocument, ref: string): Sprite | undefined {
  const found = findSprite(doc, ref);
  // `=== false` narrows under the Service's loose test tsconfig too.
  if (found.ok !== false) return found.sprite;
  const list = found.sprites.map((s) => `${s.id} "${s.name}"`).join(', ');
  errors.push(err(found.code, `${found.message}; sprites: ${list || 'none'}`, 'sprite'));
  return undefined;
}

/**
 * One key put in a tag's set (006's slot invariant: a key in the same time,
 * property and tag is replaced). An untagged write also replaces a
 * desktop-tagged key in its slot, so the base set keeps one key per slot.
 */
function put(
  track: Keyframe[], property: string, timeMs: number, value: number | string, tag: string | undefined,
  newId: NewKeyframeId, easing?: KeyframeEasing,
): Keyframe[] {
  const kf: Keyframe = {
    id: newId(), time: timeMs, property, value,
    ...(easing ? { easing } : {}),
    ...(tag ? { orientation: tag as Orientation } : {}),
  };
  const rest = tag ? track : track.filter((k) => !(k.orientation === 'desktop' && k.time === timeMs && k.property === property));
  return putKeyframe(rest, kf);
}

/** D-6: the set's keys of `property` strictly between `fromMs` and `toMs` go. */
const clearBetween = (track: Keyframe[], property: string, tag: string | undefined, fromMs: number, toMs: number) =>
  track.filter((k) => !(inSet(k, property, tag) && k.time > fromMs && k.time < toMs));

/**
 * D-4, before a scoped write of `property`: portrait or landscape forks (006);
 * desktop first keeps portrait and landscape as they play, each with its own
 * copy of the untagged keys or, when there are none, one own key holding
 * the value it plays (so the untagged keys written next do not reach it).
 * Without a scope, nothing.
 */
function prepareScope(track: Keyframe[], sprite: Sprite, property: string, scope: Scope, atMs: number, newId: NewKeyframeId): Keyframe[] {
  const tag = tagOf(scope);
  if (tag) return forkOrientation(track, property, tag, newId).keyframes;
  if (scope !== 'desktop') return track;
  let next = track;
  for (const o of ['portrait', 'landscape'] as const) {
    if (hasOwn(next, property, o)) continue;
    if (next.some((k) => k.property === property && isUntaggedKeyframe(k))) {
      next = forkOrientation(next, property, o, newId).keyframes;
    } else {
      next = put(next, property, atMs, keyframeBaseValue(sprite, property, o), o, newId);
    }
  }
  return next;
}

const played = (sprite: Sprite, track: readonly Keyframe[], property: string, timeMs: number, o: Orientation) =>
  keyframeValueAt(sprite, track, property, Math.max(0, timeMs), o);

// ─── 14. moveSprite ─────────────────────────────────────────────────────────

export interface MoveSpriteArgs {
  sprite: string;
  at: number;
  to: Partial<Record<MoveField, number>>;
  over?: number;
  ease?: KeyframeEasing;
  orientation?: Orientation;
}

const num2 = (v: number) => `${Math.round(v * 100) / 100}`;

function toText(to: MoveSpriteArgs['to']): string {
  const parts: string[] = [];
  if (to.x !== undefined) parts.push(`x ${num2(to.x)}`);
  if (to.y !== undefined) parts.push(`y ${num2(to.y)}`);
  if (to.scale !== undefined) parts.push(`scale ${num2(to.scale)}`);
  if (to.rotation !== undefined) parts.push(`rotation ${num2(to.rotation)}°`);
  if (to.opacity !== undefined) parts.push(`opacity ${num2(to.opacity)}`);
  if (to.hingeX !== undefined && to.hingeY !== undefined) parts.push(`hinge ${num2(to.hingeX)},${num2(to.hingeY)}`);
  else if (to.hingeX !== undefined) parts.push(`hingeX ${num2(to.hingeX)}`);
  else if (to.hingeY !== undefined) parts.push(`hingeY ${num2(to.hingeY)}`);
  return parts.join(', ');
}

export const moveSprite: SceneOperation<MoveSpriteArgs> = {
  name: 'moveSprite',
  description: 'Give a character a position, size, rotation, opacity or hinge at a time, written as keyframes: to holds the values it has at `at` seconds; with over it glides there from where it is over that many seconds before (ease shapes the glide). Keys of those values inside the glide are replaced.',
  inputSchema: obj({
    sprite: SPRITE_SCHEMA,
    at: AT_SCHEMA,
    to: TO_SCHEMA,
    over: num(`Seconds it takes to get there, ending at \`at\` (above 0, at most ${KEY_RANGES.span[1]} and at most at); omit to set the values at that moment.`),
    ease: KEY_EASE_SCHEMA,
    orientation: KEY_ORIENTATION_SCHEMA,
  }, ['sprite', 'at', 'to']),
  apply(doc, args) {
    const errors: Errors = [];
    const sprite = lookUp(errors, doc, args.sprite);
    const to = args.to ?? {};
    const fields = MOVE_FIELDS.filter((f) => to[f] !== undefined);
    if (fields.length === 0) errors.push(err('invalid_args', `to needs at least one of ${MOVE_FIELDS.join(', ')}`, 'to'));
    for (const f of fields) inRange(errors, to[f], KEY_RANGES[f], `to.${f}`, f === 'x' || f === 'y' ? '%' : f === 'rotation' ? ' degrees' : '');
    inRange(errors, args.at, KEY_RANGES.time, 'at', ' s');
    inSpan(errors, args.over, 'over');
    if (args.over !== undefined && args.over > args.at) errors.push(err('out_of_range', `over must be at most at (${args.at} s): the glide starts at the scene start at the earliest`, 'over'));
    if (args.ease !== undefined && args.over === undefined) errors.push(err('invalid_args', 'ease shapes a glide; give over with it', 'ease'));
    if (args.ease !== undefined && !KEYFRAME_EASINGS.includes(args.ease)) errors.push(err('invalid_args', `ease must be one of ${KEYFRAME_EASINGS.join(', ')}`, 'ease'));
    if (errors.length || !sprite) return fail(errors);

    const scope = args.orientation;
    const tag = tagOf(scope);
    const endMs = secToMs(args.at);
    const startMs = args.over !== undefined ? Math.max(0, endMs - secToMs(args.over)) : undefined;
    const newId = motionKeyframeId(doc, sprite.id);
    let track = [...trackOf(doc, sprite.id)];

    for (const f of fields) {
      const property = storedProperty(f as KeyProperty);
      const value = toStoredValue(property, to[f]!) as number;
      // D-5: without a scope, each orientation starts from where it is.
      const own: { o: 'portrait' | 'landscape'; from: number | string }[] = [];
      let from: number | string | undefined;
      if (startMs !== undefined) {
        from = played(sprite, track, property, startMs, playsIn(scope));
        if (scope === undefined) {
          for (const o of ['portrait', 'landscape'] as const) {
            if (hasOwn(track, property, o)) continue;
            const v = played(sprite, track, property, startMs, o);
            if (v !== from) own.push({ o, from: v });
          }
        }
      }
      track = prepareScope(track, sprite, property, scope, startMs ?? endMs, newId);
      for (const { o } of own) track = forkOrientation(track, property, o, newId).keyframes;
      if (startMs !== undefined) {
        track = clearBetween(track, property, tag, startMs, endMs);
        track = put(track, property, startMs, from!, tag, newId, args.ease);
      }
      track = put(track, property, endMs, value, tag, newId);
      for (const { o, from: v } of own) {
        track = clearBetween(track, property, o, startMs!, endMs);
        track = put(track, property, startMs!, v, o, newId, args.ease);
        track = put(track, property, endMs, value, o, newId);
      }
    }
    const over = args.over !== undefined ? ` (over ${fmtS(args.over)})` : '';
    return {
      ok: true,
      doc: withTrack(doc, sprite.id, track),
      summary: `${sprite.name}: moves to ${toText(to)} at ${fmtS(args.at)}${over}${scopeText(scope)}`,
      createdIds: [],
    };
  },
};

// ─── 15. setSpriteState ─────────────────────────────────────────────────────

export interface SetSpriteStateArgs {
  sprite: string;
  state: string;
  at: number;
  until?: number;
  orientation?: Orientation;
}

export const setSpriteState: SceneOperation<SetSpriteStateArgs> = {
  name: 'setSpriteState',
  description: 'Switch a character to one of its states (an expression or animation) at a time, written as a state keyframe; with until it switches back to the state it had before at that time.',
  inputSchema: obj({
    sprite: SPRITE_SCHEMA,
    state: str('One of the sprite\'s state names, e.g. neutral.'),
    at: AT_SCHEMA,
    until: num(`Seconds from the scene start when it switches back (${r('time')}, later than at); omit to keep the state.`),
    orientation: KEY_ORIENTATION_SCHEMA,
  }, ['sprite', 'state', 'at']),
  apply(doc, args) {
    const errors: Errors = [];
    const sprite = lookUp(errors, doc, args.sprite);
    if (sprite && !sprite.states.some((s) => s.name === args.state)) {
      errors.push(err('not_found', `no state "${args.state}" on ${sprite.name}; its states: ${sprite.states.map((s) => s.name).join(', ')}`, 'state'));
    }
    inRange(errors, args.at, KEY_RANGES.time, 'at', ' s');
    inRange(errors, args.until, KEY_RANGES.time, 'until', ' s');
    if (args.until !== undefined && !(args.until > args.at)) errors.push(err('out_of_range', `until must be later than at (${args.at} s)`, 'until'));
    if (errors.length || !sprite) return fail(errors);

    const scope = args.orientation;
    const tag = tagOf(scope);
    const atMs = secToMs(args.at);
    const newId = motionKeyframeId(doc, sprite.id);
    let track = prepareScope([...trackOf(doc, sprite.id)], sprite, 'state', scope, atMs, newId);
    // The state it plays just before `at`: the latest counting key before it, else the default.
    const before = candidateKeyframes(track, 'state', playsIn(scope)).filter((k) => k.time < atMs).pop();
    const prior = before ? before.value : sprite.defaultState || (sprite.states[0]?.name ?? '');
    if (args.until !== undefined) track = clearBetween(track, 'state', tag, atMs, secToMs(args.until));
    track = put(track, 'state', atMs, args.state, tag, newId);
    if (args.until !== undefined) track = put(track, 'state', secToMs(args.until), prior, tag, newId);
    const when = args.until !== undefined ? ` ${fmtS(args.at)}–${fmtS(args.until)}` : ` from ${fmtS(args.at)}`;
    return {
      ok: true,
      doc: withTrack(doc, sprite.id, track),
      summary: `${sprite.name}: ${args.state}${when}${scopeText(scope)}`,
      createdIds: [],
    };
  },
};

// ─── 16. showSprite / 17. hideSprite ────────────────────────────────────────

export interface ShowHideSpriteArgs {
  sprite: string;
  at: number;
  fade?: number;
  orientation?: Orientation;
}

function checkShowHide(errors: Errors, args: ShowHideSpriteArgs) {
  inRange(errors, args.at, KEY_RANGES.time, 'at', ' s');
  inSpan(errors, args.fade, 'fade');
}

export const showSprite: SceneOperation<ShowHideSpriteArgs> = {
  name: 'showSprite',
  description: 'Make a character appear at a time (hidden until then), at once or fading in over fade seconds from that time; written as opacity keyframes.',
  inputSchema: obj({
    sprite: SPRITE_SCHEMA,
    at: AT_SCHEMA,
    fade: num(`Seconds the fade-in takes, starting at at (above 0, at most ${KEY_RANGES.span[1]}); omit to appear at once.`),
    orientation: KEY_ORIENTATION_SCHEMA,
  }, ['sprite', 'at']),
  apply(doc, args) {
    const errors: Errors = [];
    const sprite = lookUp(errors, doc, args.sprite);
    checkShowHide(errors, args);
    if (errors.length || !sprite) return fail(errors);

    const scope = args.orientation;
    const tag = tagOf(scope);
    const atMs = secToMs(args.at);
    const newId = motionKeyframeId(doc, sprite.id);
    let track = prepareScope([...trackOf(doc, sprite.id)], sprite, 'opacity', scope, atMs, newId);
    if (atMs > 0) {
      // Showing at a time means hidden until then.
      const keyedBefore = candidateKeyframes(track, 'opacity', playsIn(scope)).some((k) => k.time < atMs);
      if (!keyedBefore && (played(sprite, track, 'opacity', atMs - 1, playsIn(scope)) as number) > 0) {
        track = put(track, 'opacity', 0, 0, tag, newId);
      }
    }
    if (args.fade === undefined) {
      if (atMs > 0) track = put(track, 'opacity', atMs - 1, 0, tag, newId);
      track = put(track, 'opacity', atMs, 1, tag, newId);
    } else {
      const endMs = atMs + secToMs(args.fade);
      track = clearBetween(track, 'opacity', tag, atMs, endMs);
      track = put(track, 'opacity', atMs, 0, tag, newId);
      track = put(track, 'opacity', endMs, 1, tag, newId);
    }
    const fade = args.fade !== undefined ? ` (fade ${fmtS(args.fade)})` : '';
    return { ok: true, doc: withTrack(doc, sprite.id, track), summary: `${sprite.name}: shown at ${fmtS(args.at)}${fade}${scopeText(scope)}`, createdIds: [] };
  },
};

export const hideSprite: SceneOperation<ShowHideSpriteArgs> = {
  name: 'hideSprite',
  description: 'Make a character leave at a time, at once or fading out over fade seconds from that time; written as opacity keyframes.',
  inputSchema: obj({
    sprite: SPRITE_SCHEMA,
    at: AT_SCHEMA,
    fade: num(`Seconds the fade-out takes, starting at at (above 0, at most ${KEY_RANGES.span[1]}); omit to disappear at once.`),
    orientation: KEY_ORIENTATION_SCHEMA,
  }, ['sprite', 'at']),
  apply(doc, args) {
    const errors: Errors = [];
    const sprite = lookUp(errors, doc, args.sprite);
    checkShowHide(errors, args);
    if (errors.length || !sprite) return fail(errors);

    const scope = args.orientation;
    const tag = tagOf(scope);
    const atMs = secToMs(args.at);
    const newId = motionKeyframeId(doc, sprite.id);
    let track = prepareScope([...trackOf(doc, sprite.id)], sprite, 'opacity', scope, atMs, newId);
    if (args.fade === undefined) {
      if (atMs > 0) track = put(track, 'opacity', atMs - 1, played(sprite, track, 'opacity', atMs - 1, playsIn(scope)), tag, newId);
      track = put(track, 'opacity', atMs, 0, tag, newId);
    } else {
      const endMs = atMs + secToMs(args.fade);
      const from = played(sprite, track, 'opacity', atMs, playsIn(scope));
      track = clearBetween(track, 'opacity', tag, atMs, endMs);
      track = put(track, 'opacity', atMs, from, tag, newId);
      track = put(track, 'opacity', endMs, 0, tag, newId);
    }
    const fade = args.fade !== undefined ? ` (fade ${fmtS(args.fade)})` : '';
    return { ok: true, doc: withTrack(doc, sprite.id, track), summary: `${sprite.name}: hidden at ${fmtS(args.at)}${fade}${scopeText(scope)}`, createdIds: [] };
  },
};

// ─── 18. flipSprite ─────────────────────────────────────────────────────────

export interface FlipSpriteArgs {
  sprite: string;
  at: number;
  flipX?: boolean;
  flipY?: boolean;
  orientation?: Orientation;
}

const FLIP_AXES = ['flipX', 'flipY'] as const;

/**
 * Spec 038 D-4: mirror a sprite's picture from a time, one stepped flip key
 * (`1` flipped, `0` as drawn) per given axis, through 006's writer with 036's
 * lookup (D-10), orientation rule (D-4) and derived ids (D-11).
 */
export const flipSprite: SceneOperation<FlipSpriteArgs> = {
  name: 'flipSprite',
  description: 'Mirror a character\'s picture from a time, written as flip keyframes: flipX true turns it to face the other way left to right (a character drawn facing left then faces right), false shows it as drawn again; flipY mirrors it top to bottom. It holds until the next flip key; there is no glide.',
  inputSchema: obj({
    sprite: SPRITE_SCHEMA,
    at: AT_SCHEMA,
    flipX: { type: 'boolean', description: 'true = mirrored left to right from at, false = as drawn from at.' },
    flipY: { type: 'boolean', description: 'true = mirrored top to bottom from at, false = as drawn from at.' },
    orientation: KEY_ORIENTATION_SCHEMA,
  }, ['sprite', 'at']),
  apply(doc, args) {
    const errors: Errors = [];
    const sprite = lookUp(errors, doc, args.sprite);
    const axes = FLIP_AXES.filter((a) => args[a] !== undefined);
    if (axes.length === 0) errors.push(err('invalid_args', 'give flipX, flipY or both', 'flipX'));
    for (const a of axes) if (typeof args[a] !== 'boolean') errors.push(err('invalid_args', `${a} must be true or false`, a));
    inRange(errors, args.at, KEY_RANGES.time, 'at', ' s');
    if (errors.length || !sprite) return fail(errors);

    const scope = args.orientation;
    const tag = tagOf(scope);
    const atMs = secToMs(args.at);
    const newId = motionKeyframeId(doc, sprite.id);
    let track = [...trackOf(doc, sprite.id)];
    for (const a of axes) {
      const property = storedProperty(a);
      track = prepareScope(track, sprite, property, scope, atMs, newId);
      track = put(track, property, atMs, toStoredValue(property, args[a]!), tag, newId);
    }
    // `Pirate: flipX on at 6s, flipY off`
    const [first, ...rest] = axes.map((a) => `${a} ${args[a] ? 'on' : 'off'}`);
    return {
      ok: true,
      doc: withTrack(doc, sprite.id, track),
      summary: `${sprite.name}: ${first} at ${fmtS(args.at)}${rest.map((p) => `, ${p}`).join('')}${scopeText(scope)}`,
      createdIds: [],
    };
  },
};
